import { motion } from "framer-motion";
import { Heart, ShieldCheck, Sparkles, Award, Check } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { HEART } from "../lib/data";

const VALUES = [
  { icon: Heart, title: "Warm & Approachable", desc: "We treat your home and office like our own — friendly faces you'll be glad to welcome back." },
  { icon: ShieldCheck, title: "Registered & Insured", desc: "Registered, insured and bondable, with police clearance available whenever it's needed or requested." },
  { icon: Sparkles, title: "Detail-Obsessed", desc: "It's the little finishing touches that turn clean into truly cared-for." },
  { icon: Award, title: "Award-Winning & Local", desc: "Proudly award-winning and rooted in the Hamilton community we serve." },
];

export default function About() {
  return (
    <>
      <SEO
        title="Meet Wisp"
        description="Wisp Home Cleaning is a warm, locally owned cleaning company serving Hamilton and surrounding communities with residential and small-office cleaning."
        image="/about-team.jpg"
      />
      <section className="border-b border-slate-200 bg-white px-5 pt-16 pb-14 md:px-10">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Meet Wisp</span>
            <h1 className="mt-3 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Making life feel a little lighter
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              A clean home can't solve everything — but it can bring a sense of calm, comfort, and
              breathing room.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              Sometimes having one less responsibility on your shoulders can make all the difference.
              That's what we hope to give everyone we serve: a home that feels easier to enjoy, and
              more time and energy for whatever matters most to them.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              We're proudly local, owned and operated and caring for homes across Hamilton and the
              surrounding communities since 2019. And we always start with a free home visit — so we
              can understand your space, listen to what matters to you, and give honest pricing that
              fits. No formulas, no surprises, no pressure.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl"
          >
            <img
              src="/about-team.jpg"
              alt="Warm, approachable Wisp cleaning team in royal blue t-shirts and blue jeans"
              className="h-[420px] w-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section className="px-5 py-20 md:px-10">
        <div className="mx-auto max-w-7xl">
          <h2 className="max-w-2xl font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
            What we stand for
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white px-5 py-20 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">The heart of Wisp</span>
            <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              More than simply cleaning
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Wisp is dependable, caring help in the background — the kind that makes life feel a
              little lighter. It comes down to a few things we hold close:
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2">
            {HEART.map((h, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`heart-item-${i}`}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 p-5"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-4 w-4 text-primary" />
                </span>
                <span className="text-slate-700">{h}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-5 pb-20 md:px-10">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl bg-primary p-8 md:p-14">
          <p className="font-heading text-xs font-semibold uppercase tracking-[0.25em] text-white/70">We understand</p>
          <p className="mt-5 font-heading text-2xl font-light leading-snug tracking-tight text-white sm:text-3xl">
            We know life gets full. Between work, family, and everything in between, cleaning is
            often the first thing to slip — and the last thing you want to spend a rare free evening
            on. That's where we come in. Whatever season of life you're in, we're here to lighten the
            load and give a little time back to you.
          </p>
        </div>
      </section>

      <CTABand />
    </>
  );
}
